<template>
    <div class="tw-w-full tw-h-full">
        <!-- Filters and Search -->
        <div class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-6">
            <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-items-start md:tw-items-center tw-justify-between">
                <!-- Filters -->
                <div class="tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-gap-3 md:tw-items-center">
                    <!-- Left filters -->
                    <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                        <!-- Status Filter -->
                        <a-button
                            v-for="status in statusFilters"
                            :key="status.value"
                            :intent="selectedStatus === status.value ? 'primary' : 'light'"
                            size="sm"
                            @click="handleStatusFilter(status.value)"
                        >
                            {{ status.label }}
                        </a-button>

                        <!-- Upcoming Toggle -->
                        <a-button
                            :intent="upcomingOnly ? 'primary' : 'light'"
                            size="sm"
                            left-icon="solar:calendar-bold"
                            @click="toggleUpcoming"
                        >
                            {{ $t('common.upcoming', 'Upcoming') }}
                        </a-button>
                    </div>

                    <!-- View Mode Toggle aligned to end -->
                    <div class="tw-flex tw-gap-1 tw-w-full md:tw-w-auto tw-justify-start md:tw-justify-end md:tw-ml-auto">
                        <a-button
                            :intent="viewMode === 'grid' ? 'primary' : 'light'"
                            size="sm"
                            left-icon="mynaui:grid"
                            @click="setViewMode('grid')"
                        >
                            {{ $t('common.grid', 'Grid') }}
                        </a-button>
                        <a-button
                            :intent="viewMode === 'list' ? 'primary' : 'light'"
                            size="sm"
                            left-icon="solar:list-bold-duotone"
                            @click="setViewMode('list')"
                        >
                            {{ $t('common.list', 'List') }}
                        </a-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-4">
            <div v-for="i in 6" :key="i" class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-animate-pulse">
                <div class="tw-flex tw-items-start tw-justify-between tw-mb-4">
                    <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                        <div class="tw-w-12 tw-h-12 tw-bg-gray-200 tw-rounded-full"></div>
                        <div class="tw-flex-1">
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4"></div>
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/2"></div>
                        </div>
                    </div>
                    <div class="tw-w-20 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                </div>
                <div class="tw-space-y-2">
                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-2/3"></div>
                </div>
                <div class="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                    <div class="tw-w-16 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                    <div class="tw-w-16 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                </div>
            </div>
        </div>

        <!-- Meetings List -->
        <div v-else-if="meetings.length > 0">
            <!-- Grid View -->
            <template v-if="viewMode === 'grid'">
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-5">
                    <div
                        v-for="meeting in meetings"
                        :key="meeting.id"
                        class="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5 tw-h-full tw-flex tw-flex-col tw-gap-5 hover:tw-shadow-lg tw-transition-shadow tw-duration-200"
                    >
                <!-- Header -->
                <div class="tw-flex tw-items-start tw-gap-3">
                    <!-- Organizer Avatar -->
                    <div class="tw-relative tw-shrink-0">
                        <img
                            v-if="meeting.organizer?.profile_image"
                            :src="meeting.organizer.profile_image"
                            :alt="meeting.organizer.name"
                            class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
                        />
                        <div
                            v-else
                            class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-purple-500 tw-flex tw-items-center tw-justify-center"
                        >
                            <span class="tw-text-white tw-text-sm tw-font-medium">
                                {{ getInitials(meeting.organizer?.name || 'Unknown') }}
                            </span>
                        </div>
                    </div>

                    <div class="tw-flex-1 tw-min-w-0">
                        <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
                            <h3 class="tw-text-base tw-font-semibold tw-text-gray-900 tw-line-clamp-2">
                                {{ meeting.title }}
                            </h3>
                            <span
                                :class="[
                                    'tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-shrink-0',
                                    getStatusBadgeClass(meeting.status)
                                ]"
                            >
                                {{ getStatusText(meeting.status) }}
                            </span>
                        </div>
                        <div class="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                            <span class="tw-inline-flex tw-items-center tw-gap-1">
                                <icon icon="solar:user-bold" class="tw-text-[11px]" />
                                {{ meeting.organizer?.name || 'Unknown' }}
                            </span>
                            <span class="tw-text-gray-300">•</span>
                            <span class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-text-gray-700 tw-px-2 tw-py-0.5 tw-rounded-full tw-capitalize">
                                <icon icon="solar:users-group-rounded-bold" class="tw-text-[11px]" />
                                {{ getMeetingTypeText(meeting.meeting_type) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Description -->
                <p v-if="meeting.description" class="tw-text-sm tw-text-gray-600 tw-line-clamp-2">
                    {{ meeting.description }}
                </p>

                <!-- Meta -->
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                    <span class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                        <icon icon="solar:calendar-bold" class="tw-text-[12px]" />
                        {{ formatMeetingDateTime(meeting.scheduled_at) }}
                    </span>
                    <span v-if="meeting.duration_minutes" class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                        <icon icon="solar:clock-circle-bold" class="tw-text-[12px]" />
                        {{ formatDuration(meeting.duration_minutes) }}
                    </span>
                    <span v-if="meeting.location" class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg tw-max-w-full">
                        <icon :icon="isVirtualMeeting(meeting) ? 'solar:monitor-bold' : 'solar:map-point-bold'" class="tw-text-[12px]" />
                        <span class="tw-truncate">{{ meeting.location }}</span>
                    </span>
                    <span v-if="meeting.participants?.length" class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                        <icon icon="solar:users-group-rounded-bold" class="tw-text-[12px]" />
                        {{ meeting.participants.length }} {{ $t('common.participants', 'participants') }}
                    </span>
                </div>

                <!-- Actions -->
                <div class="tw-flex tw-flex-wrap tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100 tw-mt-auto">
                    <a-button
                        intent="secondary"
                        size="sm"
                        left-icon="solar:eye-bold"
                        class="tw-flex-1 md:tw-flex-none"
                        @click="viewMeetingDetails(meeting)"
                    >
                        {{ $t('buttons.view', 'View') }}
                    </a-button>

                    <template v-if="isOrganizer(meeting)">
                        <a-button
                            v-if="meeting.status === 'accepted'"
                            intent="primary"
                            size="sm"
                            left-icon="solar:play-circle-bold"
                            :loading="loadingActions.has(meeting.id)"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="startMeeting(meeting)"
                        >
                            {{ $t('buttons.start', 'Start') }}
                        </a-button>

                        <a-button
                            v-if="meeting.status === 'in_progress'"
                            intent="success"
                            size="sm"
                            left-icon="solar:check-circle-bold"
                            :loading="loadingActions.has(meeting.id)"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="completeMeeting(meeting)"
                        >
                            {{ $t('buttons.complete', 'Complete') }}
                        </a-button>

                        <a-button
                            v-if="meeting.room_url && (meeting.status === 'in_progress' || meeting.status === 'accepted')"
                            intent="gradient"
                            size="sm"
                            left-icon="solar:videocamera-record-bold"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="joinMeeting(meeting)"
                        >
                            {{ $t('buttons.join', 'Join') }}
                        </a-button>
                    </template>

                    <template v-else>
                        <a-button
                            v-if="meeting.status === 'pending'"
                            intent="success"
                            size="sm"
                            left-icon="solar:check-circle-bold"
                            :loading="loadingActions.has(meeting.id)"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="acceptMeeting(meeting)"
                        >
                            {{ $t('buttons.accept', 'Accept') }}
                        </a-button>

                        <a-button
                            v-if="meeting.status === 'pending'"
                            intent="danger"
                            size="sm"
                            left-icon="solar:close-circle-bold"
                            :loading="loadingActions.has(meeting.id)"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="declineMeeting(meeting)"
                        >
                            {{ $t('buttons.decline', 'Decline') }}
                        </a-button>

                        <a-button
                            v-if="meeting.room_url && (meeting.status === 'in_progress' || meeting.status === 'accepted')"
                            intent="gradient"
                            size="sm"
                            left-icon="solar:videocamera-record-bold"
                            class="tw-flex-1 md:tw-flex-none"
                            @click="joinMeeting(meeting)"
                        >
                            {{ $t('buttons.join', 'Join') }}
                        </a-button>
                    </template>
                </div>
                    </div>
                </div>
            </template>

            <!-- List View -->
            <template v-else>
            <div class="tw-space-y-4">
                <div
                    v-for="meeting in meetings"
                    :key="meeting.id"
                    class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-5 hover:tw-shadow-md tw-transition-shadow tw-duration-200"
                >
                    <div class="tw-flex tw-flex-col gap-4">
                        <!-- Header row -->
                        <div class="tw-flex tw-items-start tw-gap-3">
                            <div class="tw-relative tw-shrink-0">
                                <img
                                    v-if="meeting.organizer?.profile_image"
                                    :src="meeting.organizer.profile_image"
                                    :alt="meeting.organizer.name"
                                    class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
                                />
                                <div
                                    v-else
                                    class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-purple-500 tw-flex tw-items-center tw-justify-center"
                                >
                                    <span class="tw-text-white tw-text-sm tw-font-medium">
                                        {{ getInitials(meeting.organizer?.name || 'Unknown') }}
                                    </span>
                                </div>
                            </div>

                            <div class="tw-flex-1 tw-min-w-0">
                                <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                    <h3 class="tw-text-base tw-font-semibold tw-text-gray-900 tw-line-clamp-1">
                                        {{ meeting.title }}
                                    </h3>
                                    <span
                                        :class="[
                                            'tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-shrink-0',
                                            getStatusBadgeClass(meeting.status)
                                        ]"
                                    >
                                        {{ getStatusText(meeting.status) }}
                                    </span>
                                </div>
                                <div class="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                                    <span class="tw-inline-flex tw-items-center tw-gap-1">
                                        <icon icon="solar:user-bold" class="tw-text-[11px]" />
                                        {{ meeting.organizer?.name || 'Unknown' }}
                                    </span>
                                    <span class="tw-text-gray-300">•</span>
                                    <span class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-text-gray-700 tw-px-2 tw-py-0.5 tw-rounded-full tw-capitalize">
                                        <icon icon="solar:users-group-rounded-bold" class="tw-text-[11px]" />
                                        {{ getMeetingTypeText(meeting.meeting_type) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Description -->
                        <p v-if="meeting.description" class="tw-text-sm tw-text-gray-600 tw-line-clamp-2">
                            {{ meeting.description }}
                        </p>

                        <!-- Meta row -->
                        <div class="tw-flex tw-flex-wrap tw-gap-2 tw-text-xs tw-font-medium tw-text-gray-700">
                            <span class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                                <icon icon="solar:calendar-bold" class="tw-text-[12px]" />
                                {{ formatMeetingDateTime(meeting.scheduled_at) }}
                            </span>
                            <span v-if="meeting.duration_minutes" class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                                <icon icon="solar:clock-circle-bold" class="tw-text-[12px]" />
                                {{ formatDuration(meeting.duration_minutes) }}
                            </span>
                            <span v-if="meeting.location" class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg tw-max-w-full">
                                <icon :icon="isVirtualMeeting(meeting) ? 'solar:monitor-bold' : 'solar:map-point-bold'" class="tw-text-[12px]" />
                                <span class="tw-truncate">{{ meeting.location }}</span>
                            </span>
                            <span v-if="meeting.participants?.length" class="tw-inline-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-lg">
                                <icon icon="solar:users-group-rounded-bold" class="tw-text-[12px]" />
                                {{ meeting.participants.length }} {{ $t('common.participants', 'participants') }}
                            </span>
                        </div>

                        <!-- Actions -->
                        <div class="tw-flex tw-flex-wrap tw-gap-3 tw-pt-3 tw-border-t tw-border-gray-100">
                            <a-button
                                intent="secondary"
                                size="sm"
                                left-icon="solar:eye-bold"
                                class="tw-flex-1 md:tw-flex-none"
                                @click="viewMeetingDetails(meeting)"
                            >
                                {{ $t('buttons.view', 'View') }}
                            </a-button>

                            <template v-if="isOrganizer(meeting)">
                                <a-button
                                    v-if="meeting.status === 'accepted'"
                                    intent="primary"
                                    size="sm"
                                    left-icon="solar:play-circle-bold"
                                    :loading="loadingActions.has(meeting.id)"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="startMeeting(meeting)"
                                >
                                    {{ $t('buttons.start', 'Start') }}
                                </a-button>

                                <a-button
                                    v-if="meeting.status === 'in_progress'"
                                    intent="success"
                                    size="sm"
                                    left-icon="solar:check-circle-bold"
                                    :loading="loadingActions.has(meeting.id)"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="completeMeeting(meeting)"
                                >
                                    {{ $t('buttons.complete', 'Complete') }}
                                </a-button>

                                <a-button
                                    v-if="meeting.room_url && (meeting.status === 'in_progress' || meeting.status === 'accepted')"
                                    intent="gradient"
                                    size="sm"
                                    left-icon="solar:videocamera-record-bold"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="joinMeeting(meeting)"
                                >
                                    {{ $t('buttons.join', 'Join') }}
                                </a-button>
                            </template>

                            <template v-else>
                                <a-button
                                    v-if="meeting.status === 'pending'"
                                    intent="success"
                                    size="sm"
                                    left-icon="solar:check-circle-bold"
                                    :loading="loadingActions.has(meeting.id)"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="acceptMeeting(meeting)"
                                >
                                    {{ $t('buttons.accept', 'Accept') }}
                                </a-button>

                                <a-button
                                    v-if="meeting.status === 'pending'"
                                    intent="danger"
                                    size="sm"
                                    left-icon="solar:close-circle-bold"
                                    :loading="loadingActions.has(meeting.id)"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="declineMeeting(meeting)"
                                >
                                    {{ $t('buttons.decline', 'Decline') }}
                                </a-button>

                                <a-button
                                    v-if="meeting.room_url && (meeting.status === 'in_progress' || meeting.status === 'accepted')"
                                    intent="gradient"
                                    size="sm"
                                    left-icon="solar:videocamera-record-bold"
                                    class="tw-flex-1 md:tw-flex-none"
                                    @click="joinMeeting(meeting)"
                                >
                                    {{ $t('buttons.join', 'Join') }}
                                </a-button>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            </template>
        </div>

        <!-- Empty State -->
        <div v-else class="tw-text-center tw-py-12">
            <div class="tw-max-w-md tw-mx-auto">
                <div class="tw-w-24 tw-h-24 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                    <icon icon="solar:users-group-rounded-bold" class="tw-text-4xl tw-text-gray-400" />
                </div>
                <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                    {{ $t('messages.no-meetings-found', 'No meetings found') }}
                </h3>
                <p class="tw-text-gray-500 tw-mb-6">
                    {{ getEmptyStateMessage() }}
                </p>
                <a-button
                    v-if="hasActiveFilters"
                    intent="primary"
                    left-icon="solar:refresh-bold"
                    @click="resetFilters"
                >
                    {{ $t('buttons.reset-filters', 'Reset Filters') }}
                </a-button>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="meetings.length > 0" class="tw-mt-6">
            <TablePagination
                :has-next="!!pagination.next_page_url"
                :has-previous="!!pagination.prev_page_url"
                @next="loadNextPage"
                @previous="loadPreviousPage"
            />
        </div>

        <!-- Meeting Details Popup -->
        <MeetingDetailsPopup
            v-if="selectedMeeting"
            :visible="showMeetingPopup"
            :notification="popupNotification"
            @close="closeMeetingPopup"
            @meetingAccepted="handleMeetingAccepted"
            @meetingDeclined="handleMeetingDeclined"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores';
import meetingsApi from '@/api/meetings';
import useAlert from '@/composables/useAlert';
import TablePagination from '@/components/table/TablePagination.vue';
import MeetingDetailsPopup from '@/components/notifications/modals/MeetingDetailsPopup.vue';

const { t } = useI18n();
const authStore = useAuthStore();

// Reactive data
const loading = ref(false);
const meetings = ref([]);
const pagination = ref({});
const loadingActions = ref(new Set());
const searchQuery = ref('');
const selectedStatus = ref('all');
const upcomingOnly = ref(false);
const currentPage = ref(1);
const showMeetingPopup = ref(false);
const selectedMeeting = ref(null);
const viewMode = ref('grid'); // 'grid' | 'list'

// Status filters
const statusFilters = [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'pending', label: t('meeting-status.pending', 'Pending') },
    { value: 'accepted', label: t('meeting-status.accepted', 'Accepted') },
    { value: 'declined', label: t('meeting-status.declined', 'Declined') },
    { value: 'cancelled', label: t('meeting-status.cancelled', 'Cancelled') },
    { value: 'in_progress', label: t('meeting-status.in-progress', 'In Progress') },
    { value: 'completed', label: t('meeting-status.completed', 'Completed') }
];

// Computed properties
const hasActiveFilters = computed(() => {
    return selectedStatus.value !== 'all' || 
           upcomingOnly.value || 
           searchQuery.value.trim() !== '';
});

// Popup notification format (MeetingDetailsPopup expects notification format)
const popupNotification = computed(() => {
    if (!selectedMeeting.value) return null;
    
    const meeting = selectedMeeting.value;
    
    // Transform participants to include name directly
    const transformedParticipants = meeting.participants?.map(participant => ({
        ...participant,
        name: participant.user?.name || participant.name || 'Unknown'
    })) || [];
    
    // Transform meeting data to match popup expectations
    const transformedMeeting = {
        ...meeting,
        start_time: meeting.start_time || meeting.scheduled_at,
        end_time: meeting.end_time || (meeting.scheduled_at && meeting.duration_minutes 
            ? new Date(new Date(meeting.scheduled_at).getTime() + meeting.duration_minutes * 60000).toISOString()
            : null),
        meeting_url: meeting.meeting_url || meeting.room_url || meeting.host_room_url,
        location: meeting.location || null,
        participants: transformedParticipants
    };
    
    return {
        id: meeting.id,
        title: meeting.title,
        data: {
            meeting: transformedMeeting
        }
    };
});

// Methods
const getMyMeetings = async (page = 1) => {
    loading.value = true;
    try {
        const params = {
            page
        };

        // Add status filter
        if (selectedStatus.value !== 'all') {
            params.status = selectedStatus.value;
        }

        // Add upcoming filter
        if (upcomingOnly.value) {
            params.upcoming = true;
        }

        // Add search query (client-side filtering for now)
        // Note: API might support search, but implementing client-side for flexibility

        const response = await meetingsApi.paginate(params);
        const responseData = response.data || {};
        const payload = responseData.data || {};

        // Update meetings (payload.data is the list from API spec)
        if (payload.data && Array.isArray(payload.data)) {
            let filteredMeetings = payload.data;
            
            // Client-side search filtering
            if (searchQuery.value.trim()) {
                const query = searchQuery.value.trim().toLowerCase();
                filteredMeetings = filteredMeetings.filter(meeting => 
                    meeting.title?.toLowerCase().includes(query) ||
                    meeting.description?.toLowerCase().includes(query) ||
                    meeting.organizer?.name?.toLowerCase().includes(query)
                );
            }
            
            meetings.value = filteredMeetings;
        } else {
            meetings.value = [];
        }

        // Update pagination
        if (payload.current_page) {
            pagination.value = {
                current_page: payload.current_page,
                last_page: payload.last_page,
                next_page_url: payload.next_page_url,
                prev_page_url: payload.prev_page_url,
                total: payload.total
            };
        } else {
            pagination.value = {};
        }

        currentPage.value = page;
    } catch (error) {
        console.error('Error fetching meetings:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong. Please try again.'), 'danger');
        meetings.value = [];
    } finally {
        loading.value = false;
    }
};

const handleStatusFilter = (status) => {
    selectedStatus.value = status;
    currentPage.value = 1;
    getMyMeetings(1);
};

const toggleUpcoming = () => {
    upcomingOnly.value = !upcomingOnly.value;
    currentPage.value = 1;
    getMyMeetings(1);
};

const resetFilters = () => {
    searchQuery.value = '';
    selectedStatus.value = 'all';
    upcomingOnly.value = false;
    currentPage.value = 1;
    getMyMeetings(1);
};

const setViewMode = (mode) => {
    viewMode.value = mode;
};

const loadNextPage = () => {
    if (pagination.value.next_page_url) {
        getMyMeetings(currentPage.value + 1);
    }
};

const loadPreviousPage = () => {
    if (pagination.value.prev_page_url) {
        getMyMeetings(currentPage.value - 1);
    }
};

// Action handlers
const acceptMeeting = async (meeting) => {
    loadingActions.value.add(meeting.id);
    try {
        await meetingsApi.accept(meeting.id);
        
        // Update local state
        const index = meetings.value.findIndex(m => m.id === meeting.id);
        if (index !== -1) {
            meetings.value[index].status = 'accepted';
        }
        
        useAlert(t('messages.meeting-accepted-success', `Meeting "${meeting.title}" accepted successfully`), 'success');
        
        // Refresh list
        getMyMeetings(currentPage.value);
    } catch (error) {
        console.error('Error accepting meeting:', error);
        const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
        useAlert(errorMessage, 'danger');
    } finally {
        loadingActions.value.delete(meeting.id);
    }
};

const declineMeeting = async (meeting) => {
    loadingActions.value.add(meeting.id);
    try {
        await meetingsApi.decline(meeting.id, {
            reason: 'Declined via meetings tab'
        });
        
        // Update local state
        const index = meetings.value.findIndex(m => m.id === meeting.id);
        if (index !== -1) {
            meetings.value[index].status = 'declined';
        }
        
        useAlert(t('messages.meeting-declined-success', `Meeting "${meeting.title}" declined`), 'success');
        
        // Refresh list
        getMyMeetings(currentPage.value);
    } catch (error) {
        console.error('Error declining meeting:', error);
        const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
        useAlert(errorMessage, 'danger');
    } finally {
        loadingActions.value.delete(meeting.id);
    }
};

const startMeeting = async (meeting) => {
    loadingActions.value.add(meeting.id);
    try {
        const response = await meetingsApi.start(meeting.id);
        const updatedMeeting = response.data?.data;
        
        // Update local state
        const index = meetings.value.findIndex(m => m.id === meeting.id);
        if (index !== -1) {
            if (updatedMeeting) {
                meetings.value[index] = { ...meetings.value[index], ...updatedMeeting };
            } else {
                meetings.value[index].status = 'in_progress';
            }
        }
        
        useAlert(t('messages.meeting-started-success', `Meeting "${meeting.title}" started`), 'success');
        
        // Refresh list
        getMyMeetings(currentPage.value);
    } catch (error) {
        console.error('Error starting meeting:', error);
        const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
        useAlert(errorMessage, 'danger');
    } finally {
        loadingActions.value.delete(meeting.id);
    }
};

const completeMeeting = async (meeting) => {
    loadingActions.value.add(meeting.id);
    try {
        await meetingsApi.complete(meeting.id);
        
        // Update local state
        const index = meetings.value.findIndex(m => m.id === meeting.id);
        if (index !== -1) {
            meetings.value[index].status = 'completed';
        }
        
        useAlert(t('messages.meeting-completed-success', `Meeting "${meeting.title}" completed`), 'success');
        
        // Refresh list
        getMyMeetings(currentPage.value);
    } catch (error) {
        console.error('Error completing meeting:', error);
        const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
        useAlert(errorMessage, 'danger');
    } finally {
        loadingActions.value.delete(meeting.id);
    }
};

const joinMeeting = (meeting) => {
    const roomUrl = isOrganizer(meeting)
        ? (meeting.host_room_url || meeting.room_url)
        : (meeting.room_url || meeting.host_room_url);

    if (roomUrl) {
        window.open(roomUrl, '_blank');
    } else {
        useAlert(t('errors.no-room-url', 'Meeting room URL not available'), 'warning');
    }
};

const viewMeetingDetails = async (meeting) => {
    try {
        // Fetch full meeting details
        const response = await meetingsApi.get(meeting.id);
        selectedMeeting.value = response.data?.data || meeting;
        showMeetingPopup.value = true;
    } catch (error) {
        console.error('Error fetching meeting details:', error);
        // Fallback to using the meeting data we already have
        selectedMeeting.value = meeting;
        showMeetingPopup.value = true;
    }
};

const closeMeetingPopup = () => {
    showMeetingPopup.value = false;
    selectedMeeting.value = null;
};

const handleMeetingAccepted = async (meeting) => {
    await acceptMeeting(meeting);
    closeMeetingPopup();
};

const handleMeetingDeclined = async (meeting) => {
    await declineMeeting(meeting);
    closeMeetingPopup();
};

// Helper functions
const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const isOrganizer = (meeting) => {
    return authStore.user?.id && meeting.organizer_id === authStore.user.id;
};

const isVirtualMeeting = (meeting) => {
    const location = meeting.location?.toLowerCase() || '';
    return location.includes('zoom') || location.includes('teams') || location.includes('meet') || 
           location.includes('webex') || location.includes('whereby') || meeting.room_url;
};

const formatMeetingDateTime = (dateString) => {
    if (!dateString) return '';
    
    try {
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        let dateText;
        if (date.toDateString() === today.toDateString()) {
            dateText = t('common.today', 'Today');
        } else if (date.toDateString() === tomorrow.toDateString()) {
            dateText = t('common.tomorrow', 'Tomorrow');
        } else {
            const month = date.toLocaleString(undefined, { month: 'short' });
            const day = date.getDate();
            dateText = `${month} ${day}`;
        }
        
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        const timeText = `${displayHours}:${displayMinutes} ${ampm}`;
        
        return `${dateText} at ${timeText}`;
    } catch (error) {
        console.error('Error formatting meeting date time:', error);
        return '';
    }
};

const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
        return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
};

const getMeetingTypeText = (type) => {
    const typeMap = {
        'member_to_member': t('meeting-types.member-to-member', 'Member to Member'),
        'member_to_company': t('meeting-types.member-to-company', 'Member to Company')
    };
    
    return typeMap[type] || type || t('meeting-types.meeting', 'Meeting');
};

const getStatusText = (status) => {
    const statusMap = {
        'pending': t('meeting-status.pending', 'Pending'),
        'accepted': t('meeting-status.accepted', 'Accepted'),
        'declined': t('meeting-status.declined', 'Declined'),
        'cancelled': t('meeting-status.cancelled', 'Cancelled'),
        'in_progress': t('meeting-status.in-progress', 'In Progress'),
        'completed': t('meeting-status.completed', 'Completed')
    };
    
    return statusMap[status] || status || 'Unknown';
};

const getStatusBadgeClass = (status) => {
    const statusClasses = {
        'pending': 'tw-bg-yellow-100 tw-text-yellow-700',
        'accepted': 'tw-bg-green-100 tw-text-green-700',
        'declined': 'tw-bg-red-100 tw-text-red-700',
        'cancelled': 'tw-bg-gray-100 tw-text-gray-700',
        'in_progress': 'tw-bg-purple-100 tw-text-purple-700',
        'completed': 'tw-bg-emerald-100 tw-text-emerald-700'
    };
    
    return statusClasses[status] || 'tw-bg-gray-100 tw-text-gray-700';
};

const getEmptyStateMessage = () => {
    if (hasActiveFilters.value) {
        return t('messages.no-meetings-for-filter', 'No meetings found matching your filters. Try adjusting your search criteria.');
    }
    return t('messages.no-meetings-description', 'You don\'t have any meetings scheduled yet.');
};

// Lifecycle
onMounted(() => {
    getMyMeetings();
});
</script>

<style scoped>
</style>
